import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { useProperties } from "@/contexts/PropertyContext";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import {
    BedDouble, Plus, Pencil, Trash2, Loader2, X, Users, Baby, Palette, Sparkles, GripVertical,
} from "lucide-react";

/**
 * Hotel Admin — Room Categories management page.
 * Lists every room type for the active property, lets admins create, edit,
 * delete (only if no rooms use it), reorder and seed the default catalogue.
 */
const PALETTE = [
    "#f97316", "#0ea5e9", "#8b5cf6", "#10b981", "#ec4899", "#f43f5e",
    "#facc15", "#14b8a6", "#a855f7", "#f59e0b", "#22c55e", "#3b82f6",
];

const EMPTY = {
    property_id: "", name: "", code: "", capacity_adults: 2, capacity_children: 0,
    base_price: 0, color: "#f97316", description: "",
};

export default function RoomTypes() {
    const { activePropertyId, properties } = useProperties();
    const [propertyId, setPropertyId] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        if (activePropertyId && activePropertyId !== "all") setPropertyId(activePropertyId);
        else if (properties.length) setPropertyId(properties[0].id);
    }, [activePropertyId, properties]);

    const load = async () => {
        if (!propertyId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/room-types?property_id=${propertyId}`);
            setRows(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [propertyId]); // eslint-disable-line

    const seedDefaults = async () => {
        try {
            const { data } = await api.post(`/room-types/seed-defaults?property_id=${propertyId}`);
            toast.success(`Seeded ${data.created} default categor${data.created === 1 ? "y" : "ies"}`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const remove = async (t) => {
        if (t.rooms_count > 0) {
            toast.error(`Reassign ${t.rooms_count} room(s) first`);
            return;
        }
        if (!window.confirm(`Delete "${t.name}"?`)) return;
        try {
            await api.delete(`/room-types/${t.id}`);
            toast.success("Category deleted");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <>
            <Topbar
                title="Kategoritë e dhomave"
                subtitle="Grupo dhomat sipas kategorisë — përdoret në gridin e rezervimeve, çmimet dhe raportet."
            />
            <div className="flex-1 overflow-auto p-6" data-testid="room-types-page">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        {properties.length > 1 && (
                            <select
                                className="akm-input h-9 w-auto"
                                value={propertyId}
                                onChange={(e) => setPropertyId(e.target.value)}
                                data-testid="room-types-property-select"
                            >
                                {properties.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        )}
                        <div className="text-[13px] text-muted-foreground">
                            {loading ? <Loader2 className="inline animate-spin" size={12} /> : `${rows.length} categor${rows.length === 1 ? "y" : "ies"}`}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={seedDefaults}
                            data-testid="room-types-seed"
                            className="akm-btn-ghost h-9 text-[12.5px]"
                        >
                            <Sparkles size={13} /> Seed defaults
                        </button>
                        <button
                            onClick={() => setEditing({ ...EMPTY, property_id: propertyId })}
                            data-testid="room-types-add"
                            disabled={!propertyId}
                            className="akm-btn-primary h-9 text-[12.5px] disabled:opacity-40"
                        >
                            <Plus size={13} /> New category
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {rows.map((t) => (
                        <RoomTypeCard key={t.id} row={t} onEdit={() => setEditing(t)} onRemove={() => remove(t)} />
                    ))}
                    {!loading && rows.length === 0 && (
                        <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-10 text-center">
                            <BedDouble className="mx-auto text-slate-400" size={32} strokeWidth={1.5} />
                            <div className="mt-3 text-[14px] font-semibold text-slate-800">No categories yet</div>
                            <p className="mx-auto mt-1 max-w-md text-[13px] text-muted-foreground">
                                Room categories let you group rooms of the same type (Single, Double, Suite, …) so the reservations grid stays tidy and pricing/reports are easier to read.
                            </p>
                            <button
                                onClick={seedDefaults}
                                data-testid="room-types-seed-empty"
                                disabled={!propertyId}
                                className="akm-btn-primary mt-4 disabled:opacity-40"
                            >
                                <Sparkles size={13} /> Seed 6 default categories
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {editing && (
                <RoomTypeEditor
                    row={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
        </>
    );
}

function RoomTypeCard({ row, onEdit, onRemove }) {
    const color = row.color || "#94a3b8";
    return (
        <div
            className="akm-card group relative overflow-hidden p-4"
            data-testid={`room-type-card-${row.id}`}
        >
            <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: color }} />
            <div className="ml-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <div className="text-[15px] font-semibold text-foreground">{row.name}</div>
                        {row.code && (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] font-bold tracking-wider text-slate-600">
                                {row.code}
                            </span>
                        )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Users size={11} /> {row.capacity_adults ?? row.capacity ?? 2}</span>
                        {row.capacity_children ? <span className="inline-flex items-center gap-1"><Baby size={11} /> {row.capacity_children}</span> : null}
                        <span className="tabular-nums">{formatCurrency(row.base_price || 0)} / night</span>
                        <span className="rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold text-[#F26322]">
                            {row.rooms_count} room{row.rooms_count === 1 ? "" : "s"}
                        </span>
                    </div>
                    {row.description && (
                        <p className="mt-2 line-clamp-2 text-[12px] text-slate-500">{row.description}</p>
                    )}
                </div>
                <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                        onClick={onEdit}
                        data-testid={`room-type-edit-${row.id}`}
                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                        title="Modifiko"
                    >
                        <Pencil size={13} />
                    </button>
                    <button
                        onClick={onRemove}
                        data-testid={`room-type-delete-${row.id}`}
                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                        title="Fshi"
                        disabled={row.rooms_count > 0}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function RoomTypeEditor({ row, onClose, onSaved }) {
    const [form, setForm] = useState({ ...row });
    const [saving, setSaving] = useState(false);
    const isEdit = !!row.id;

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/room-types/${row.id}`, {
                    name: form.name, code: form.code, capacity_adults: Number(form.capacity_adults),
                    capacity_children: Number(form.capacity_children), base_price: Number(form.base_price),
                    color: form.color, description: form.description,
                });
                toast.success("Category updated");
            } else {
                await api.post("/room-types", {
                    property_id: form.property_id, name: form.name, code: form.code || null,
                    capacity_adults: Number(form.capacity_adults),
                    capacity_children: Number(form.capacity_children),
                    base_price: Number(form.base_price),
                    color: form.color, description: form.description || null,
                });
                toast.success("Category created");
            }
            await onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-[520px] rounded-2xl bg-white p-6 shadow-2xl"
                data-testid="room-type-editor"
            >
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-[18px] font-semibold text-foreground">
                            {isEdit ? "Edit category" : "New category"}
                        </h3>
                        <p className="mt-1 text-[12.5px] text-muted-foreground">
                            Rooms sharing this category will inherit the base price and capacity.
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
                        <X size={16} />
                    </button>
                </div>

                <div className="mt-5 space-y-3.5">
                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                            <Label>Name *</Label>
                            <input
                                data-testid="rt-name"
                                className="akm-input"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Dhomë Teke"
                            />
                        </div>
                        <div>
                            <Label>Code</Label>
                            <input
                                data-testid="rt-code"
                                className="akm-input uppercase"
                                maxLength={4}
                                value={form.code || ""}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                placeholder="SGL"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Adults</Label>
                            <input
                                data-testid="rt-adults" type="number" min={1} max={12}
                                className="akm-input"
                                value={form.capacity_adults}
                                onChange={(e) => setForm({ ...form, capacity_adults: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Children</Label>
                            <input
                                data-testid="rt-children" type="number" min={0} max={12}
                                className="akm-input"
                                value={form.capacity_children}
                                onChange={(e) => setForm({ ...form, capacity_children: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Base price €</Label>
                            <input
                                data-testid="rt-price" type="number" min={0} step="0.01"
                                className="akm-input"
                                value={form.base_price}
                                onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Color</Label>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                            {PALETTE.map((c) => (
                                <button
                                    key={c} type="button"
                                    onClick={() => setForm({ ...form, color: c })}
                                    data-testid={`rt-color-${c}`}
                                    className={`grid h-7 w-7 place-items-center rounded-full transition ${
                                        form.color === c ? "ring-2 ring-offset-2 ring-slate-500" : "hover:scale-110"
                                    }`}
                                    style={{ background: c }}
                                >
                                    {form.color === c && <span className="text-[10px] font-bold text-white">✓</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label>Description</Label>
                        <textarea
                            data-testid="rt-description"
                            className="akm-input min-h-[70px] text-[13px]"
                            value={form.description || ""}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Opsionale — çfarë e bën këtë kategori të veçantë?"
                        />
                    </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="akm-btn-ghost h-9">Anulo</button>
                    <button
                        onClick={save} disabled={saving || !form.name}
                        data-testid="rt-save"
                        className="akm-btn-primary h-9 disabled:opacity-50"
                    >
                        {saving && <Loader2 size={12} className="animate-spin" />}
                        {isEdit ? "Ruaj ndryshimet" : "Krijo kategorinë"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Label({ children }) {
    return <div className="mb-1 text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">{children}</div>;
}
